// 1:1 port from age-of-mcp v3 (src/client/models/v3/signpost.ts) — the
// flattened part literals rebuilt from the original helpers + loops.
//
// v3 signpost — A WAYFINDING POST. One sturdy oak post under a little
// terracotta pyramid cap, with three pale ARROW arms (plank + pointed tip)
// at stepped heights pointing three ways — the arrow silhouette reads as
// "directions" from any camera yaw. Inscribed, so it never reorients.

import { defineModel, styleV3, type Part } from "../../lib";

const { TIMBER, CANVAS, STONE_DK, ROOF_HOME, box, cyl, slab, poles, pyramid } = styleV3;

const BASE   = 0.04;          // a thin ground pad
const POST_H = 0.5;

function pad(): Part {
  return slab(0.16, 0.16, STONE_DK, BASE);
}

function post(h: number): Part {
  return box(0.05, h, 0.05, TIMBER, [0, BASE + h / 2, 0]);
}

// One arrow arm at height `y`: a plank plus a 4-sided cone tip, pointing
// east (+x), west (-x) or north (+z).
function arm(y: number, dir: "e" | "w" | "n"): Part[] {
  if (dir === "n") {
    return [
      box(0.03, 0.06, 0.24, CANVAS, [0, y, 0.14]),
      cyl(0, 0.05, 0.09, 4, CANVAS, [0, y, 0.3], [Math.PI / 2, 0, 0]),
    ];
  }
  const sgn = dir === "e" ? 1 : -1;
  return [
    box(0.24, 0.06, 0.03, CANVAS, [sgn * 0.14, y, 0]),
    cyl(0, 0.05, 0.09, 4, CANVAS, [sgn * 0.3, y, 0], [0, 0, -sgn * Math.PI / 2]),
  ];
}

function partsFor(s: number): Part[] {
  if (s <= 0) return [pad()];
  if (s === 1) return [pad(), post(0.2), ...poles(0.2, 0.2, 0.24)];
  if (s === 2) return [pad(), post(POST_H), ...poles(0.2, 0.2, 0.4)];

  return [
    pad(),
    post(POST_H),
    // The terracotta cap.
    pyramid(0.09, 0.05, ROOF_HOME, 0, BASE + POST_H, 0),
    // Three arrow arms at stepped heights, pointing three ways.
    ...arm(0.46, "e"),
    ...arm(0.37, "w"),
    ...arm(0.28, "n"),
  ];
}

export const signpostV3 = defineModel({
  id: "signpost-v3",
  tiles: { w: 1, h: 1 },
  batchId: "45-optimized",
  name: "Signpost (v3)",
  tags: ["building","signpost","low-poly","age-of-mcp","v3"],
  summary: "Low-poly signpost.",
  description: "A post with arms, each carrying a short label pointing a compass direction.",
  author: "age-of-mcp",
  footprint: 0.13,
  defaultVariant: "complete",
  variants: [
    { id: "stage-0", label: "Foundation", parts: () => partsFor(0) },
    { id: "stage-1", label: "Lower", parts: () => partsFor(1) },
    { id: "stage-2", label: "Upper", parts: () => partsFor(2) },
    { id: "complete", label: "Complete", parts: () => partsFor(3) },
  ],
  animations: [],
});
